import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Copy, Eye, Plus, Trash2, Undo2 } from "lucide-react";

/**
 * Governo della vetrina: aperta o chiusa, chiusura automatica a una data e un link per canale
 * (social, whatsapp, singolo locale) con il suo contatore di visite e il suo «revoca».
 */
export const VetrinaAdmin = ({ tenant, client, onDone }) => {
  const [s, setS] = useState({ open: true, until: "", keys: [] });
  const [nome, setNome] = useState("");
  const base = `${window.location.origin}/vetrina`;

  const carica = useCallback(async () => {
    const r = await client.get(`/admin/tenants/${tenant.id}/demo-access`);
    setS(r.data);
  }, [client, tenant.id]);

  useEffect(() => {
    carica();
  }, [carica]);

  const azione = async (fn, msg) => {
    try {
      const r = await fn();
      if (r?.data?.keys || r?.data?.open !== undefined) setS((v) => ({ ...v, ...r.data }));
      toast.success(msg);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Non riuscito");
    }
  };

  const copia = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Link copiato");
  };

  return (
    <div className="space-y-3 border-t border-slate-800 pt-3" data-testid="vetrina-admin">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Vetrina</span>
        <label className="flex items-center gap-2 text-xs text-slate-300">
          <Switch checked={s.open !== false} data-testid="vetrina-open-switch"
                  onCheckedChange={(v) => azione(() => client.post(`/admin/tenants/${tenant.id}/demo-access`, { open: v }),
                                                 v ? "Vetrina aperta" : "Vetrina chiusa")} />
          {s.open !== false ? "aperta a tutti" : "chiusa"}
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-400">
          chiudi da sola il
          <Input type="date" value={s.until || ""} className="h-8 w-[150px] bg-slate-950" data-testid="vetrina-until-input"
                 onChange={(e) => azione(() => client.post(`/admin/tenants/${tenant.id}/demo-access`, { until: e.target.value }),
                                         e.target.value ? "Chiusura programmata" : "Scadenza rimossa")} />
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" data-testid="vetrina-copy-generic" onClick={() => copia(base)}>
          <Copy className="mr-1 h-3.5 w-3.5" /> Copia link generico
        </Button>
        {s.open === false && (
          <span className="rounded-full bg-rose-900/60 px-2 py-1 text-[10px] font-bold uppercase text-rose-200">
            i link non entrano più
          </span>
        )}
      </div>

      <div className="space-y-1.5">
        <p className="text-[11px] text-slate-500">
          Un link per canale: vedi da dove arrivano i curiosi e chiudi solo quello che non ti serve più.
        </p>
        {(s.keys || []).map((k) => (
          <div key={k.k} className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-800 p-2"
               data-testid={`vetrina-key-${k.k}`}>
            <span className={`text-sm font-semibold ${k.revoked ? "text-slate-500 line-through" : "text-slate-200"}`}>
              {k.label}
            </span>
            <span className="font-num text-[11px] text-amber-500" data-testid={`vetrina-hits-${k.k}`}>
              {k.hits} visite
            </span>
            <span className="min-w-0 flex-1 truncate text-[11px] text-slate-500">{`${base}?k=${k.k}`}</span>
            <Button size="sm" variant="ghost" className="h-7 text-[11px] text-slate-300" data-testid={`vetrina-copy-${k.k}`}
                    onClick={() => copia(`${base}?k=${k.k}`)}>
              <Copy className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-[11px] text-slate-300" data-testid={`vetrina-revoke-${k.k}`}
                    onClick={() => azione(() => client.post(`/admin/tenants/${tenant.id}/demo-keys/${k.k}/revoke`, { undo: !!k.revoked }),
                                          k.revoked ? "Link riattivato" : "Link revocato")}>
              {k.revoked ? <Undo2 className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-[11px] text-rose-300" data-testid={`vetrina-del-${k.k}`}
                    onClick={() => azione(() => client.delete(`/admin/tenants/${tenant.id}/demo-keys/${k.k}`), "Link eliminato")}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
        <div className="flex gap-2">
          <Input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Instagram, WhatsApp, Osteria Rossi…"
                 className="h-9 bg-slate-950" data-testid="vetrina-key-name" />
          <Button size="sm" className="h-9" data-testid="vetrina-key-add"
                  onClick={() => nome.trim() && azione(async () => {
                    const r = await client.post(`/admin/tenants/${tenant.id}/demo-keys`, { label: nome.trim() });
                    setNome("");
                    return r;
                  }, "Link creato")}>
            <Plus className="mr-1 h-3.5 w-3.5" /> Nuovo link
          </Button>
        </div>
      </div>
    </div>
  );
};
